import React from "react";
import { setAnalyticsPushDeferEnabled } from "./analytics-defer";
import type { AnalyticsDeferMode } from "../analytics-flags";

/**
 * WAT-11459: arm / disarm the post-paint analytics queue from the mode the
 * server resolved for THIS page.
 *
 * Lives here rather than inline in pages/_app.tsx for two reasons: any
 * `*.spec.*` file under pages/ is compiled by Next as a real route (verified —
 * a co-located pages/_app.spec.tsx shipped as `ƒ /_app.spec`, 37.3 kB), and
 * _app.tsx should stay thin wiring.
 *
 * `mode` is already page-scoped by resolvePagesRouterDeferMode in
 * getServerSideProps, so "toggle off" and "'home' mode on a non-home page" both
 * arrive as "off"/undefined. This hook never re-checks the page.
 *
 * The arm/disarm asymmetry is deliberate, not an oversight:
 * - ARM runs in the RENDER phase. It is an idempotent module-level assignment
 *   (safe under StrictMode's double render) and has to be live before any child
 *   fires analytics from its own mount effect — which rules out an effect here,
 *   since child effects run first.
 * - DISARM runs POST-COMMIT. setAnalyticsPushDeferEnabled(false) DRAINS the
 *   queue (see analytics-defer.ts), and draining inside a render pass React may
 *   discard would fire queued analytics early. Reaching the effect with a
 *   non-empty queue means a soft nav onto an unarmed page, where an immediate
 *   flush is the correct handoff back to the legacy synchronous path.
 *
 * Arming also runs during SSR and mutates the server module's flag. That is
 * harmless: runAnalyticsTask's `typeof window` guard keeps every server-side
 * task synchronous regardless (see its doc comment).
 *
 * ⚠️ The render-phase arm is only safe because Pages Router navigation is NOT
 * wrapped in startTransition. A discarded render would arm with no effect to
 * undo it. Revisit this if navigation ever moves into a transition.
 */
export function useAnalyticsDeferArm(mode: AnalyticsDeferMode | undefined): void {
  const deferMode = mode || "off";
  // ALLOWLIST, not `!== "off"`. The fetch layer guarantees a typo can only ever
  // narrow the rollout; a denylist here would invert that at the last step and
  // arm on any unrecognized string that slipped through.
  const enabled = deferMode === "home" || deferMode === "all";

  if (enabled) {
    setAnalyticsPushDeferEnabled(true);
  }

  React.useEffect(() => {
    if (!enabled) {
      setAnalyticsPushDeferEnabled(false);
    }
    // Diagnostic marker: which mode this document actually resolved to. Read by
    // the synthetic defer suite, which lives OUTSIDE this repo at
    // uf-wt/gtm-audit/defer-synthetic-suite.mjs (sibling worktree) — keep the
    // { remote, effective } shape in sync with it.
    //
    // Pages Router only, and deliberately NOT mirrored into App Router: an
    // ABSENT marker on an App Router document is the correct, expected reading
    // ("no defer in v1", see app/layout.tsx), not missing instrumentation. The
    // suite must treat undefined as BAU rather than as a failed arm.
    // eslint-disable-next-line no-underscore-dangle
    (window as any).__analyticsDeferState = {
      remote: deferMode,
      effective: enabled
    };
  }, [deferMode, enabled]);
}
